import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import { MergePolygonsFeature } from '@kb3d/models/classes/features/merge-polygons-feature';
import { earcut } from '@kb3d/view/helpers/earcut';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';

import { FeatureGeometryProcessor, ProcessorData } from './base-processor';

const NORMAL_EPSILON = 1e-3;
const MERGE_POLYGONS_VERSION = '1.8.0';

export class MergePolygonsGeometryProcessor extends FeatureGeometryProcessor<MergePolygonsFeature> {
    updateMeshGeometry(
        feature: MergePolygonsFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<never>
    ): Partial<SubmeshVertexData> {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);
        // console.log(`[MergePolygons v${MERGE_POLYGONS_VERSION}]`);

        if (!newVertexData.indices || !newVertexData.positions || feature.vertexGroups.length === 0) {
            return newVertexData;
        }

        const indices = newVertexData.indices;
        const positions = newVertexData.positions;

        // ── Step 1: Find all fully-selected triangles ──────────────────────────
        const selectedTriStarts = this.getSelectedTriStarts(feature.vertexGroups, indices);
        if (selectedTriStarts.size === 0) {
            return newVertexData;
        }

        // ── Step 2: Group by normal — process each face independently ─────────
        // Each normal group (one planar face) is triangulated independently by
        // triangulateGroup(); then all results are merged in step 10.
        const normalGroups = this.groupTrisByNormal(selectedTriStarts, indices, positions);
        const allActiveTriStarts = new Set<number>();
        const allRebuiltIndices: number[] = [];

        for (const groupTriStarts of normalGroups) {
            for (const ts of groupTriStarts) allActiveTriStarts.add(ts);
            allRebuiltIndices.push(...this.triangulateGroup(groupTriStarts, indices, positions));
        }

        if (allRebuiltIndices.length === 0) {
            return newVertexData;
        }

        // ── Step 10: Mark old selected triangles, adjust submeshData, splice ──
        const markedIndices = Array.from(indices);
        for (const triStart of allActiveTriStarts) {
            markedIndices[triStart]     = -1;
            markedIndices[triStart + 1] = -1;
            markedIndices[triStart + 2] = -1;
        }

        if (newVertexData.submeshData && newVertexData.submeshData.length > 0) {
            // Build a prefix-sum of how many -1 entries exist up to each position
            // so we can cheaply compute the shift for each submesh boundary.
            const removedCount = new Int32Array(markedIndices.length + 1);
            for (let i = 0; i < markedIndices.length; i++) {
                removedCount[i + 1] = removedCount[i] + (markedIndices[i] === -1 ? 1 : 0);
            }
            for (const submesh of newVertexData.submeshData) {
                const start = submesh.indexStart;
                const end   = start + submesh.indexCount;
                submesh.indexStart  -= removedCount[start];
                submesh.indexCount  -= (removedCount[end] - removedCount[start]);
            }
            // Append rebuilt triangles to the last submesh
            const lastSubmesh = newVertexData.submeshData[newVertexData.submeshData.length - 1];
            lastSubmesh.indexCount += allRebuiltIndices.length;
        }

        const filtered = markedIndices.filter(idx => idx !== -1);
        newVertexData.indices = new Int32Array([...filtered, ...allRebuiltIndices]);

        return newVertexData;
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private triangulateGroup(
        activeTriStarts: Set<number>,
        indices: ArrayLike<number>,
        positions: ArrayLike<number>
    ): number[] {
        // ── Step 3: Build local 2D frame from the face normal ─────────────────
        const firstTriStart = activeTriStarts.values().next().value as number;
        const faceNormal = this.computeTriNormal(firstTriStart, indices, positions);
        const arbitrary = Math.abs(faceNormal.x) <= Math.abs(faceNormal.y) ? Vector3.Right() : Vector3.Up();
        const vVec = Vector3.Normalize(arbitrary.subtract(faceNormal.scale(Vector3.Dot(arbitrary, faceNormal))));
        const uVec = Vector3.Normalize(vVec.cross(faceNormal));

        const project2D = (vIdx: number): [number, number] => [
            positions[vIdx * 3] * uVec.x + positions[vIdx * 3 + 1] * uVec.y + positions[vIdx * 3 + 2] * uVec.z,
            positions[vIdx * 3] * vVec.x + positions[vIdx * 3 + 1] * vVec.y + positions[vIdx * 3 + 2] * vVec.z,
        ];

        // ── Step 4: Canonicalize vertex indices by snapped position ──────────
        const posKeyToCanonical = new Map<string, number>();
        const getCanonical = (vIdx: number): number => {
            const pk = this.posKey(positions, vIdx);
            if (!posKeyToCanonical.has(pk)) posKeyToCanonical.set(pk, vIdx);
            return posKeyToCanonical.get(pk)!;
        };

        // ── Step 5: Build directed boundary edges ─────────────────────────────
        const allDirEdges = new Set<string>();
        for (const triStart of activeTriStarts) {
            const v0 = getCanonical(indices[triStart]);
            const v1 = getCanonical(indices[triStart + 1]);
            const v2 = getCanonical(indices[triStart + 2]);
            for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]] as [number, number][]) {
                if (a !== b) allDirEdges.add(`${a}\u2192${b}`);
            }
        }

        const nextVertices = new Map<number, number[]>();
        for (const edgeKey of allDirEdges) {
            const sep = edgeKey.indexOf('\u2192');
            const a = parseInt(edgeKey.slice(0, sep));
            const b = parseInt(edgeKey.slice(sep + '\u2192'.length));
            if (!allDirEdges.has(`${b}\u2192${a}`)) {
                if (!nextVertices.has(a)) nextVertices.set(a, []);
                nextVertices.get(a)!.push(b);
            }
        }

        if (nextVertices.size === 0) return [];

        // ── Step 6: Trace closed boundary loops ───────────────────────────────
        const startVerticesSorted = [...nextVertices.keys()].sort((a, b) => {
            const aIsJunction = (nextVertices.get(a)?.length ?? 0) > 1 ? 1 : 0;
            const bIsJunction = (nextVertices.get(b)?.length ?? 0) > 1 ? 1 : 0;
            return aIsJunction - bIsJunction;
        });

        const usedEdges = new Set<string>();
        const loops: number[][] = [];

        for (const startVertex of startVerticesSorted) {
            if (!(nextVertices.get(startVertex) ?? []).some(n => !usedEdges.has(`${startVertex}\u2192${n}`))) continue;

            const firstNext = (nextVertices.get(startVertex) ?? []).find(n => !usedEdges.has(`${startVertex}\u2192${n}`));
            if (firstNext === undefined) continue;

            const loop: number[] = [];
            let prev: number | null = null;
            let current = startVertex;
            let next = firstNext;

            while (true) {
                usedEdges.add(`${current}\u2192${next}`);
                loop.push(current);
                prev = current;
                current = next;

                if (current === startVertex) break;

                const outgoing = (nextVertices.get(current) ?? []).filter(n => !usedEdges.has(`${current}\u2192${n}`));
                if (outgoing.length === 0) break;
                if (outgoing.length === 1) {
                    next = outgoing[0];
                } else {
                    const [prevU, prevV] = project2D(prev!);
                    const [currU, currV] = project2D(current);
                    const inU = currU - prevU, inV = currV - prevV;
                    let bestAngle = Infinity, bestNext = outgoing[0];
                    for (const candidate of outgoing) {
                        const [candU, candV] = project2D(candidate);
                        const outU = candU - currU, outV = candV - currV;
                        const angle = Math.atan2(inU * outV - inV * outU, inU * outU + inV * outV);
                        if (angle < bestAngle) { bestAngle = angle; bestNext = candidate; }
                    }
                    next = bestNext;
                }
            }

            if (loop.length >= 3) loops.push(loop);
        }

        if (loops.length === 0) return [];

        // console.log(`[MergePolygons v${MERGE_POLYGONS_VERSION}] traced ${loops.length} raw loop(s):`,
        //     loops.map((l, i) => `  loop[${i}] ${l.length} verts`).join(', '));

        // ── Step 6a: Discard spurious tiny loops ──────────────────────────────
        const rawArea = (l: number[]): number => {
            let a = 0;
            for (let i = 0; i < l.length; i++) {
                const [u0, v0] = project2D(l[i]);
                const [u1, v1] = project2D(l[(i + 1) % l.length]);
                a += u0 * v1 - u1 * v0;
            }
            return a / 2;
        };
        const rawAreas = loops.map(rawArea);
        const maxAbsArea = rawAreas.reduce((m, a) => Math.max(m, Math.abs(a)), 0);
        const AREA_THRESHOLD_REL = 1e-4;
        const significantLoops = maxAbsArea === 0
            ? loops
            : loops.filter((_, i) => Math.abs(rawAreas[i]) / maxAbsArea >= AREA_THRESHOLD_REL);

        if (significantLoops.length < loops.length) {
            // console.log(`[MergePolygons v${MERGE_POLYGONS_VERSION}] discarded ${loops.length - significantLoops.length} spurious tiny loop(s)`);
        }

        // ── Step 6b: Remove collinear vertices ────────────────────────────────
        const vertexLoopCount = new Map<number, number>();
        for (const loop of significantLoops) {
            for (const v of new Set(loop)) vertexLoopCount.set(v, (vertexLoopCount.get(v) ?? 0) + 1);
        }
        const junctionVertices = new Set<number>(
            [...vertexLoopCount.entries()].filter(([, c]) => c > 1).map(([v]) => v)
        );

        const simplifiedLoops = significantLoops
            .map(loop => this.simplifyLoop(loop, positions, junctionVertices))
            .filter(loop => loop.length >= 3);

        if (simplifiedLoops.length === 0) return [];

        // console.log(`[MergePolygons v${MERGE_POLYGONS_VERSION}] simplified ${simplifiedLoops.length} loop(s):`,
        //     simplifiedLoops.map((l, i) => `  loop[${i}] ${l.length} verts`).join(', '));

        // ── Step 7: Classify loops via signed 2D area ─────────────────────────
        const signedArea2D = (loop: number[]): number => {
            let area = 0;
            for (let i = 0; i < loop.length; i++) {
                const [u0, v0] = project2D(loop[i]);
                const [u1, v1] = project2D(loop[(i + 1) % loop.length]);
                area += u0 * v1 - u1 * v0;
            }
            return area / 2;
        };

        const outerLoops = simplifiedLoops.filter(l => signedArea2D(l) >= 0);
        const holeLoops  = simplifiedLoops.filter(l => signedArea2D(l) <  0);

        if (outerLoops.length === 0 && holeLoops.length > 0) {
            holeLoops.sort((a, b) => Math.abs(signedArea2D(b)) - Math.abs(signedArea2D(a)));
            outerLoops.push(holeLoops.shift()!);
        }

        if (outerLoops.length === 0) return [];

        const ensureCCW = (loop: number[]) => signedArea2D(loop) >= 0 ? loop : [...loop].reverse();
        const ensureCW  = (loop: number[]) => signedArea2D(loop) <  0 ? loop : [...loop].reverse();

        outerLoops.sort((a, b) => Math.abs(signedArea2D(b)) - Math.abs(signedArea2D(a)));

        // console.log(`[MergePolygons v${MERGE_POLYGONS_VERSION}] outer loops: ${outerLoops.length} (areas: ${outerLoops.map(l => signedArea2D(l).toFixed(4)).join(', ')})`);
        // console.log(`[MergePolygons v${MERGE_POLYGONS_VERSION}] hole loops: ${holeLoops.length} (areas: ${holeLoops.map(l => signedArea2D(l).toFixed(4)).join(', ')})`);

        // ── Steps 8–9: Triangulate each outer loop independently ──────────────
        const rebuiltIndices: number[] = [];

        for (const outer of outerLoops) {
            const outerCCW = ensureCCW(outer);
            const outerPts = outerCCW.map(v => project2D(v));

            const containedHoles = holeLoops.filter(h => {
                let sumU = 0, sumV = 0;
                for (const vIdx of h) { const [u, v] = project2D(vIdx); sumU += u; sumV += v; }
                const centU = sumU / h.length, centV = sumV / h.length;
                return this.pointInPolygon2D(centU, centV, outerPts);
            });

            const coords: number[] = [];
            const holeIndices: number[] = [];
            const vertexMap: number[] = [];

            for (let k = 0; k < outerPts.length; k++) {
                coords.push(outerPts[k][0], outerPts[k][1]);
                vertexMap.push(outerCCW[k]);
            }

            for (const hole of containedHoles) {
                holeIndices.push(coords.length / 2);
                for (const vIdx of ensureCW(hole)) {
                    const [u, v] = project2D(vIdx);
                    coords.push(u, v);
                    vertexMap.push(vIdx);
                }
            }

            const result = earcut(coords, holeIndices, 2);

            for (let t = 0; t < result.length; t += 3) {
                const ei0 = result[t], ei1 = result[t + 1], ei2 = result[t + 2];
                const u0 = coords[ei0 * 2], v0 = coords[ei0 * 2 + 1];
                const u1 = coords[ei1 * 2], v1 = coords[ei1 * 2 + 1];
                const u2 = coords[ei2 * 2], v2 = coords[ei2 * 2 + 1];
                const twoA = (u1 - u0) * (v2 - v0) - (u2 - u0) * (v1 - v0);
                if (Math.abs(twoA) < 1e-10) continue;
                if (twoA > 0) {
                    rebuiltIndices.push(vertexMap[ei0], vertexMap[ei1], vertexMap[ei2]);
                } else {
                    rebuiltIndices.push(vertexMap[ei0], vertexMap[ei2], vertexMap[ei1]);
                }
            }

            // console.log(`[MergePolygons v${MERGE_POLYGONS_VERSION}] outer ${outer.length}v area=${signedArea2D(outer).toFixed(4)} holes=${containedHoles.length} → ${result.length / 3} tris`);
        }

        return rebuiltIndices;
    }

    private getSelectedTriStarts(
        vertexGroups: MergePolygonsFeature['vertexGroups'],
        indices: ArrayLike<number>
    ): Set<number> {
        // Collect every index-buffer position that falls within any vertex group range
        const coveredPositions = new Set<number>();
        for (const vg of vertexGroups) {
            for (let i = Math.max(vg.startIndex, 0); i < Math.min(vg.endIndex, indices.length); i++) {
                coveredPositions.add(i);
            }
        }
        // Promote to a triangle start only when all 3 positions of the triangle are covered
        const triStarts = new Set<number>();
        for (const pos of coveredPositions) {
            const triStart = pos - (pos % 3);
            if (
                coveredPositions.has(triStart) &&
                coveredPositions.has(triStart + 1) &&
                coveredPositions.has(triStart + 2)
            ) {
                triStarts.add(triStart);
            }
        }
        return triStarts;
    }

    private computeTriNormal(triStart: number, indices: ArrayLike<number>, positions: ArrayLike<number>): Vector3 {
        const getPos = (offset: number) => {
            const vIdx = indices[triStart + offset];
            return new Vector3(positions[vIdx * 3], positions[vIdx * 3 + 1], positions[vIdx * 3 + 2]);
        };
        return getPos(1).subtract(getPos(0)).cross(getPos(2).subtract(getPos(0))).normalize();
    }

    private groupTrisByNormal(
        selectedTriStarts: Set<number>,
        indices: ArrayLike<number>,
        positions: ArrayLike<number>
    ): Set<number>[] {
        const groups: { normal: Vector3; triStarts: Set<number> }[] = [];
        for (const triStart of selectedTriStarts) {
            const normal = this.computeTriNormal(triStart, indices, positions);
            let matched = false;
            for (const group of groups) {
                // dot ≈ 1 means same direction (not just parallel — we exclude anti-parallel faces)
                if (Vector3.Dot(group.normal, normal) > 1 - NORMAL_EPSILON) {
                    group.triStarts.add(triStart);
                    matched = true;
                    break;
                }
            }
            if (!matched) {
                groups.push({ normal, triStarts: new Set([triStart]) });
            }
        }
        return groups.map(g => g.triStarts);
    }

    private undirectedEdgeKey(a: number, b: number): string {
        return a < b ? `${a}_${b}` : `${b}_${a}`;
    }

    private pointInPolygon2D(u: number, v: number, polygon: [number, number][]): boolean {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const [xi, yi] = polygon[i];
            const [xj, yj] = polygon[j];
            if ((yi > v) !== (yj > v) && u < (xj - xi) * (v - yi) / (yj - yi) + xi) {
                inside = !inside;
            }
        }
        return inside;
    }

    private simplifyLoop(loop: number[], positions: ArrayLike<number>, protected_: Set<number> = new Set()): number[] {
        // Iteratively remove vertices that are collinear with their neighbours.
        // Uses cross-product magnitude relative to edge lengths to stay numerically
        // stable for both tiny and large meshes.
        let result = [...loop];
        let changed = true;
        while (changed && result.length > 3) {
            changed = false;
            for (let i = 0; i < result.length; i++) {
                const prev = result[(i - 1 + result.length) % result.length];
                const curr = result[i];
                const next = result[(i + 1) % result.length];
                if (!protected_.has(curr) && this.isCollinear(prev, curr, next, positions)) {
                    result.splice(i, 1);
                    changed = true;
                    break;
                }
            }
        }
        return result;
    }

    private isCollinear(
        aIdx: number,
        bIdx: number,
        cIdx: number,
        positions: ArrayLike<number>
    ): boolean {
        const ax = positions[aIdx * 3], ay = positions[aIdx * 3 + 1], az = positions[aIdx * 3 + 2];
        const bx = positions[bIdx * 3], by = positions[bIdx * 3 + 1], bz = positions[bIdx * 3 + 2];
        const cx = positions[cIdx * 3], cy = positions[cIdx * 3 + 1], cz = positions[cIdx * 3 + 2];
        // AB and AC vectors
        const abx = bx - ax, aby = by - ay, abz = bz - az;
        const acx = cx - ax, acy = cy - ay, acz = cz - az;
        // Cross product AB × AC
        const crossX = aby * acz - abz * acy;
        const crossY = abz * acx - abx * acz;
        const crossZ = abx * acy - aby * acx;
        const crossLenSq = crossX * crossX + crossY * crossY + crossZ * crossZ;
        const edgeProduct = (abx * abx + aby * aby + abz * abz) * (acx * acx + acy * acy + acz * acz);
        if (edgeProduct === 0) return true;
        // Collinear when sin²(angle) ≈ 0, i.e. |cross|² / (|AB|²·|AC|²) < epsilon
        return crossLenSq / edgeProduct < 1e-10;
    }

    private posKey(positions: ArrayLike<number>, vIdx: number): string {
        // Snap to 1e-4 grid so nearly-coincident vertices from flat-shaded meshes
        // are treated as the same position when building the boundary edge map.
        const f = 10000; // 1 / 1e-4
        return `${Math.round(positions[vIdx * 3] * f)}_${Math.round(positions[vIdx * 3 + 1] * f)}_${Math.round(positions[vIdx * 3 + 2] * f)}`;
    }
}
